// src/pages/JobScheduleControlPage.js
// FIXED CODE

import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { get, patch } from 'aws-amplify/api';
import {
    Button,
    SelectField,
    Flex,
    CheckboxField,
    View,
    Heading,
    TextField,
} from '@aws-amplify/ui-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { generateJobSheetPDF } from '../utils/pdfUtils';

// --- Helper Functions ---
function formatDateNZ(isoStr) {
    if (!isoStr) return '';
    try {
        const d = new Date(isoStr);
        if (isNaN(d.getTime())) return isoStr;
        return d.toLocaleDateString('en-NZ');
    } catch (e) {
        console.warn("Error formatting date:", isoStr, e);
        return isoStr;
    }
}

function removePrefix(fullId) {
    if (!fullId) return '';
    const idx = fullId.indexOf('#');
    if (idx >= 0) {
        return fullId.substring(idx + 1);
    }
    return fullId;
}

function flattenDynamoItem(av) {
    if (!av) return null;
    if (Array.isArray(av)) return av.map(flattenDynamoItem);
    if (typeof av === 'object') {
        if ('S' in av) return av.S;
        if ('N' in av) return parseFloat(av.N);
        if ('BOOL' in av) return av.BOOL;
        if ('NULL' in av && av.NULL === true) return null;
        if ('M' in av) {
            const out = {};
            for (const [k, v] of Object.entries(av.M)) {
                out[k] = flattenDynamoItem(v);
            }
            return out;
        }
        if ('L' in av) return av.L.map(flattenDynamoItem);
    }
    return av;
}

// Updated to include adminNotes
function flattenScheduledJobs(dynamoData) {
    const out = [];
    if (!Array.isArray(dynamoData)) return out;
    for (const record of dynamoData) {
        const flatRec = {};
        for (const [k, v] of Object.entries(record)) {
            flatRec[k] = flattenDynamoItem(v);
        }

        const baseData = {
            orderId: flatRec.orderId || '',
            scheduledDate: typeof flatRec.scheduledDate === 'string' ? flatRec.scheduledDate : null,
            orderDate: typeof flatRec.orderDate === 'string' ? flatRec.orderDate : null,
            OrderStatus: flatRec.OrderStatus || 'Entered',
            totalAmount: parseFloat(flatRec.totalAmount || 0),
            source: flatRec.source || '',
            billingAddress: typeof flatRec.billingAddress === 'object' ? flatRec.billingAddress : {},
            shippingAddress: typeof flatRec.shippingAddress === 'object' ? flatRec.shippingAddress : {},
        };

        if (!Array.isArray(flatRec.items) || flatRec.items.length === 0) {
            out.push({
                ...baseData, quantity: 0, price: 0, Board_Grade: '', Box_Style: '',
                notes: '', // GT Narration
                adminNotes: '', // New Notes
                _totalTime: 0, _lineIndex: 0, length: '', width: '', height: '',
            });
        } else {
            flatRec.items.forEach((line, idx) => {
                const props = line.properties || {};
                out.push({
                    ...baseData,
                    quantity: parseFloat(line.quantity || 0),
                    price: parseFloat(line.price || 0),
                    Board_Grade: props.Board_Grade || '',
                    Box_Style: props.Box_Style || '',
                    notes: props.notes || '', // GT Narration
                    adminNotes: props.adminNotes || '', // New Notes
                    _totalTime: parseFloat(props._totalTime || 0),
                    _lineIndex: idx,
                    length: props.Length || props.length || '',
                    width: props.Width || props.width || '',
                    height: props.Height || props.height || '',
                    productId: line.productId || '',
                    Include_Lid: props.Include_Lid || '',
                    _boxesPerSheet: props._boxesPerSheet || '',
                    _totalSheets: props._totalSheets || '',
                    _totalLength: props._totalLength || '',
                    _totalWidth: props._totalWidth || '',
                });
            });
        }
    }
    return out;
}


function decimalHoursToHM(val) {
    const numVal = parseFloat(val);
    if (isNaN(numVal)) return '';
    const h = Math.floor(numVal);
    const m = Math.round((numVal - h) * 60);
    return `${h}:${String(m).padStart(2, '0')}`;
}

function totalHoursForGroup(rows) {
    return rows.reduce((sum, r) => sum + (r._totalTime || 0), 0);
}

function groupJobsByDate(rows) {
    const groups = {};
    for (const r of rows) {
        if (typeof r.scheduledDate === 'string' && r.scheduledDate.trim()) {
            const dayKey = r.scheduledDate.substring(0, 10); // YYYY-MM-DD
            if (!groups[dayKey]) groups[dayKey] = [];
            groups[dayKey].push(r);
        } else {
            const unscheduledKey = 'UNSCHEDULED';
            if (!groups[unscheduledKey]) groups[unscheduledKey] = [];
            groups[unscheduledKey].push(r);
        }
    }
    const scheduledKeys = Object.keys(groups).filter(k => k !== 'UNSCHEDULED').sort();
    const sortedDateKeys = groups['UNSCHEDULED'] ? [...scheduledKeys, 'UNSCHEDULED'] : scheduledKeys;
    return { groups, sortedDateKeys };
}

function getNextNDays(n) {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < n; i++) {
        const d = new Date(now);
        d.setDate(now.getDate() + i);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        arr.push(`${yyyy}-${mm}-${dd}`);
    }
    return arr;
}


// --- Main Component ---

export default function JobScheduleControlPage() {
    const [jobs, setJobs] = useState([]); // Original flattened list from API
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [bulkStatus, setBulkStatus] = useState('');
    const [selectedJobIds, setSelectedJobIds] = useState(new Set());
    const [isUpdatingBulk, setIsUpdatingBulk] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const getRowId = (row) => `${row.orderId}_${row._lineIndex}`;

    // --- Fetch Jobs ---
    async function fetchJobs() {
        try {
            setLoading(true);
            setErrorMsg('');
            console.log("Fetching jobs...");

            const restOperation = get({
                apiName: 'cbx_CRUD_API',
                path: '/scheduledjobs'
            });
            const response = await restOperation.response;
            const responseBody = await response.body.json();

            if (response.statusCode < 200 || response.statusCode > 299) {
                const errorDetails = typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody);
                throw new Error(`HTTP ${response.statusCode} error fetching jobs: ${errorDetails}`);
            }

            if (!Array.isArray(responseBody)) {
                throw new Error("Received invalid data format from API.");
            }

            console.log(`Fetched ${responseBody.length} raw records.`);
            const flat = flattenScheduledJobs(responseBody); // Includes adminNotes now
            console.log(`Flattened into ${flat.length} rows.`);
            setJobs(flat);
            setSelectedJobIds(new Set());
        } catch (err) {
            console.error('fetchJobs error:', err);
            let displayError = `Failed to fetch schedule data: ${err.message || 'Unknown error'}`;
            if (err.message && (err.message.toLowerCase().includes('network error') || err.message.toLowerCase().includes('failed to fetch'))) {
                displayError = 'Network Error: Could not connect to API. Check network connection, API Gateway endpoint, and CORS configuration.';
            }
            if (err.message && (err.message.toLowerCase().includes('403') || err.message.toLowerCase().includes('forbidden'))) {
                displayError = `Forbidden (403) accessing schedule. Check API Gateway permissions/authentication for GET /scheduledjobs.`;
            }
            setErrorMsg(displayError);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchJobs();
    }, []);

    // --- Calculate Total Lines per Order ID ---
    const jobsWithTotalLines = useMemo(() => {
        if (jobs.length === 0) return [];
        const lineCounts = jobs.reduce((acc, job) => {
            acc[job.orderId] = (acc[job.orderId] || 0) + 1;
            return acc;
        }, {});
        return jobs.map(job => ({
            ...job,
            _totalLines: lineCounts[job.orderId] || 0,
        }));
    }, [jobs]);


    // --- Filter Jobs based on Search Term (includes adminNotes) ---
    const filteredJobs = useMemo(() => {
        const sourceJobs = jobsWithTotalLines;
        if (!searchTerm.trim()) {
            return sourceJobs;
        }
        const lowerSearchTerm = searchTerm.toLowerCase();

        return sourceJobs.filter(job => {
            const shortId = removePrefix(job.orderId);
            const orderDateStr = formatDateNZ(job.orderDate);
            const scheduledDateStr = job.scheduledDate ? formatDateNZ(job.scheduledDate.substring(0, 10)) : '';

            const idMatches = (shortId || '').toLowerCase().includes(lowerSearchTerm);

            return (
                idMatches ||
                (job.OrderStatus || '').toLowerCase().includes(lowerSearchTerm) ||
                (job.Board_Grade || '').toLowerCase().includes(lowerSearchTerm) ||
                (job.Box_Style || '').toLowerCase().includes(lowerSearchTerm) ||
                (job.notes || '').toLowerCase().includes(lowerSearchTerm) || // Search GT Narration
                (job.adminNotes || '').toLowerCase().includes(lowerSearchTerm) || // Search new Notes
                (job.quantity?.toString() || '').includes(lowerSearchTerm) ||
                (orderDateStr || '').toLowerCase().includes(lowerSearchTerm) ||
                (scheduledDateStr || '').toLowerCase().includes(lowerSearchTerm)
            );
        });
    }, [jobsWithTotalLines, searchTerm]);


    // --- Memoized calculations ---
    const { groups, sortedDateKeys } = useMemo(() => groupJobsByDate(filteredJobs), [filteredJobs]);
    const allDateKeys = useMemo(() => {
        const extraDays = getNextNDays(3);
        const allKeysSet = new Set([...sortedDateKeys, ...extraDays]);
        const allKeysArray = Array.from(allKeysSet).sort();
        if (allKeysArray.includes('UNSCHEDULED')) {
            return [...allKeysArray.filter(k => k !== 'UNSCHEDULED'), 'UNSCHEDULED'];
        }
        return allKeysArray;
    }, [sortedDateKeys]);


    // --- Checkbox Handlers ---
    const handleSelectRow = (rowId, isChecked) => {
        setSelectedJobIds(prev => {
            const newSet = new Set(prev);
            isChecked ? newSet.add(rowId) : newSet.delete(rowId);
            return newSet;
        });
    };
    const handleSelectAllForDate = (dateKey, isChecked) => {
        const rowsInGroup = groups[dateKey] || [];
        const rowIdsInGroup = rowsInGroup.map(getRowId);
        setSelectedJobIds(prev => {
            const newSet = new Set(prev);
            if (isChecked) { rowIdsInGroup.forEach(id => newSet.add(id)); }
            else { rowIdsInGroup.forEach(id => newSet.delete(id)); }
            return newSet;
        });
    };
    const isAllSelectedForDate = (dateKey) => {
        const rowsInGroup = groups[dateKey] || [];
        if (rowsInGroup.length === 0) return false;
        return rowsInGroup.every(row => selectedJobIds.has(getRowId(row)));
    };


    // --- Drag and Drop Handlers ---
    async function updateJobScheduledDateAPI(jobId, lineIndex, newScheduledDate) {
        const apiPath = `/scheduledjobs/${encodeURIComponent(jobId)}`;
        console.log(`[API Call] PATCH ${apiPath} with body: ${JSON.stringify({ scheduledDate: newScheduledDate })}`);

        try {
            const restOperation = patch({
                apiName: 'cbx_CRUD_API',
                path: apiPath,
                options: { body: { scheduledDate: newScheduledDate } }
            });
            const response = await restOperation.response;

            if (response.statusCode < 200 || response.statusCode > 299) {
                const bodyText = await response.body.text();
                if (response.statusCode === 403) throw new Error(`Forbidden (403). Check API Gateway path/config for PATCH ${apiPath}.`);
                throw new Error(`API Error ${response.statusCode} updating ${jobId}: ${bodyText}`);
            }
            console.log(`Successfully updated schedule for ${jobId}_${lineIndex}`);
            return true;
        } catch (err) {
            console.error('Error updating scheduled date via API for', `${jobId}_${lineIndex}`, err);
            let displayError = `Failed to update schedule for ${jobId}: ${err.message || 'Unknown error'}`;
            if (err.message && (err.message.toLowerCase().includes('network error') || err.message.toLowerCase().includes('failed to fetch'))) {
                displayError = `Network error updating schedule for ${jobId}. Check connection/CORS.`;
            }
            setErrorMsg(displayError);
            return false;
        }
    }

    async function onDragEnd(result) {
        if (!result.destination) return;
        const { destination, source, draggableId } = result;
        const rowId = draggableId;
        const [draggedOrderId, draggedLineIndexStr] = rowId.split('_');
        const draggedLineIndex = parseInt(draggedLineIndexStr, 10);

        const draggedJob = jobs.find(j => getRowId(j) === rowId);
        if (!draggedJob) {
            console.error("Could not find dragged job data for id:", rowId);
            setErrorMsg(`Internal error: Could not find data for job ${rowId}`);
            return;
        }

        const sourceDateKey = source.droppableId;
        const destinationDateKey = destination.droppableId;

        if (sourceDateKey === destinationDateKey && source.index === destination.index) return;
        if (destinationDateKey === 'UNSCHEDULED' && draggedJob.scheduledDate === null) return;
        if (draggedJob.scheduledDate?.startsWith(destinationDateKey) && destinationDateKey !== 'UNSCHEDULED') return;

        const newScheduledDate = destinationDateKey === 'UNSCHEDULED' ? null : `${destinationDateKey}T00:00:00.000Z`;

        const updateSuccess = await updateJobScheduledDateAPI(draggedOrderId, draggedLineIndex, newScheduledDate);
        if (updateSuccess) {
            await fetchJobs(); // Refetch updates the original `jobs` list
        }
    }


    // --- Bulk Status Update Handler ---
    const handleApplyBulkStatus = async () => {
        if (selectedJobIds.size === 0 || !bulkStatus) {
            alert("Please select at least one job and choose a status to apply.");
            return;
        }

        setIsUpdatingBulk(true);
        setErrorMsg('');
        const updatedJobIds = Array.from(selectedJobIds);
        const errors = [];
        let successCount = 0;

        console.log(`[Bulk Update] Applying status "${bulkStatus}" to ${updatedJobIds.length} items...`);

        // Group updates by orderId to make fewer API calls if possible
        // (Backend PATCH might only update the top-level status anyway)
        const updatesByOrderId = {};
        updatedJobIds.forEach(jobRowId => {
            const [orderId] = jobRowId.split('_');
            if (orderId) {
                updatesByOrderId[orderId] = bulkStatus; // We just need the orderId
            } else {
                console.warn(`Skipping invalid job ID format for bulk update: ${jobRowId}`);
                errors.push(`Skipped invalid ID: ${jobRowId}`);
            }
        });

        const orderIdsToUpdate = Object.keys(updatesByOrderId);

        for (const orderId of orderIdsToUpdate) {
            try {
                const apiPath = `/scheduledjobs/${encodeURIComponent(orderId)}`;
                const body = { OrderStatus: bulkStatus };
                console.log(`[API Call] PATCH ${apiPath} with body: ${JSON.stringify(body)}`);

                const restOperation = patch({ apiName: 'cbx_CRUD_API', path: apiPath, options: { body: body } });
                const response = await restOperation.response;

                if (response.statusCode < 200 || response.statusCode > 299) {
                    let errorBodyText = `Failed request to ${apiPath}. Status: ${response.statusCode}.`;
                    try { errorBodyText = await response.body.text(); } catch (bodyErr) { /* ignore */ }
                    if (response.statusCode === 403) throw new Error(`Forbidden (403). Check API Gateway config for PATCH ${apiPath}.`);
                    if (response.statusCode === 404) throw new Error(`Order not found (404) at path ${apiPath}.`);
                    throw new Error(`API Error ${response.statusCode} for ${orderId}: ${errorBodyText}`);
                }
                console.log(`Successfully updated status for order ${orderId}`);
                // Count successes based on originally selected *line items* belonging to this orderId
                successCount += updatedJobIds.filter(id => id.startsWith(`${orderId}_`)).length;
            } catch (err) {
                console.error(`Failed to update status for order ${orderId}:`, err);
                let specificErrorMsg = err.message || 'Unknown error';
                if (err.message?.includes('Network') || err.message?.includes('fetch')) specificErrorMsg = 'Network Error';
                else if (err.message?.includes('403')) specificErrorMsg = 'Forbidden (403)';
                else if (err.message?.includes('404')) specificErrorMsg = 'Not Found (404)';
                else if (err.message?.includes('50')) specificErrorMsg = 'Server Error';
                errors.push(`Failed Order ${orderId}: ${specificErrorMsg}`);
                // Keep track of failed line items for accurate reporting if needed
                // errors.push(...updatedJobIds.filter(id => id.startsWith(`${orderId}_`)).map(fid => `Failed ${fid}: ${specificErrorMsg}`));
            }
        } // End Loop

        setIsUpdatingBulk(false);

        if (errors.length > 0) {
            // Report errors based on orders that failed
            const errorSummary = errors.slice(0, 5).join('; ');
            setErrorMsg(`Failed ${errors.length} order updates. Errors: ${errorSummary}${errors.length > 5 ? '...' : ''}`);
            await fetchJobs(); // Refresh on partial failure
        } else {
            alert(`Successfully applied status "${bulkStatus}" to ${successCount} selected items across ${orderIdsToUpdate.length} orders.`);
            await fetchJobs(); // Refetch after success too
            setSelectedJobIds(new Set());
            setBulkStatus('');
        }
    };

    // --- Render Logic ---
    const renderControls = () => (
        <Flex direction="row" alignItems="flex-end" gap="1rem" margin="1rem 0" wrap="wrap">
            <TextField
                label="Search Schedule"
                placeholder="Search Order ID, Status, Notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                outerStartComponent={null}
                style={{ minWidth: '300px' }}
            />
            <SelectField label="Bulk Action" labelHidden placeholder="Choose status..." value={bulkStatus}
                         onChange={(e) => setBulkStatus(e.target.value)} size="small" disabled={isUpdatingBulk} >
                <option value="">-- Select Status --</option>
                <option value="Entered">Entered</option>
                <option value="In Progress">In Progress</option>
                <option value="On-Hold">On-Hold</option>
                {/* Add other relevant statuses */}
            </SelectField>
            <Button size="small" variation="primary" onClick={handleApplyBulkStatus}
                    disabled={selectedJobIds.size === 0 || !bulkStatus || isUpdatingBulk} isLoading={isUpdatingBulk} >
                Apply to Selected ({selectedJobIds.size})
            </Button>
        </Flex>
    );

    if (loading && jobs.length === 0) return <View padding="1rem">Loading schedule...</View>;

    return (
        <View padding="1rem">
            <Heading level={3} marginBottom="1rem">Job Schedule Control</Heading>
            {errorMsg && <View padding="1rem" style={{ border: '1px solid red', color: 'red', backgroundColor: '#ffeeee', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>Error: {errorMsg}</View>}
            {renderControls()}
            {loading && <View>Refreshing schedule...</View>}

            <DragDropContext onDragEnd={onDragEnd}>
                {filteredJobs.length === 0 && !loading && !errorMsg ? (
                    <View>No jobs match your search criteria or schedule is empty.</View>
                ) : (
                    allDateKeys.map(dateKey => {
                        const rowsForDate = groups[dateKey] || [];
                        const sumHours = totalHoursForGroup(rowsForDate);
                        const hoursStr = decimalHoursToHM(sumHours);
                        const isAllSelected = isAllSelectedForDate(dateKey);
                        const dateDisplay = dateKey === 'UNSCHEDULED' ? 'Unscheduled' : formatDateNZ(`${dateKey}T00:00:00`);

                        return (
                            <View key={dateKey} marginBottom="2rem">
                                <Heading level={4} style={{ borderBottom: '1px solid #ccc', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                                    {dateDisplay} - Total Hours: {hoursStr} ({rowsForDate.length} items shown)
                                </Heading>
                                <Droppable droppableId={dateKey}>
                                    {(provided, snapshot) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ overflowX: 'auto' }}> {/* Added wrapper for horizontal scroll */}
                                            <table
                                                style={ tableStyle(snapshot.isDraggingOver) } className="schedule-table" >
                                                <thead>
                                                <tr>
                                                    <th style={cellStyle} width="3%">
                                                        <CheckboxField label="" labelHidden name={`select-all-${dateKey}`} value="all"
                                                                       checked={isAllSelected} onChange={(e) => handleSelectAllForDate(dateKey, e.target.checked)}
                                                                       disabled={rowsForDate.length === 0} size="small" />
                                                    </th>
                                                    <th style={cellStyle}>Order/PO</th>
                                                    <th style={cellStyle}>Order Date</th>
                                                    <th style={cellStyle}>Status</th>
                                                    <th style={cellStyle}>Qty</th>
                                                    <th style={cellStyle}>Board Grade</th>
                                                    <th style={cellStyle}>Box Style</th>
                                                    <th style={cellStyle}>Est. Hours</th>
                                                    <th style={cellStyle}>GT Narration</th> {/* Changed Label */}
                                                    <th style={cellStyle}>Notes</th> {/* Added New Notes */}
                                                    <th style={cellStyle} width="10%">Actions</th> {/* Adjusted width slightly */}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {rowsForDate.length === 0 && (
                                                    <tr><td colSpan="11" style={emptyRowStyle(snapshot.isDraggingOver)}>
                                                        {dateKey === 'UNSCHEDULED' ? 'No unscheduled jobs matching search' : (snapshot.isDraggingOver ? 'Drop job here' : 'No jobs scheduled for this day matching search')}
                                                    </td></tr>
                                                )}
                                                {rowsForDate.map((row, idx) => {
                                                    const rowId = getRowId(row);
                                                    const isSelected = selectedJobIds.has(rowId);
                                                    const orderDateStr = formatDateNZ(row.orderDate);
                                                    const hrsFmt = decimalHoursToHM(row._totalTime);
                                                    const lineQty = parseFloat(row.quantity || 0);

                                                    const baseShortId = removePrefix(row.orderId);
                                                    const displayId = row._totalLines > 1
                                                        ? `${baseShortId}-${row._lineIndex + 1}`
                                                        : baseShortId;

                                                    return (
                                                        <Draggable key={rowId} draggableId={rowId} index={idx}>
                                                            {(provided2, snapshot2) => (
                                                                <tr ref={provided2.innerRef} {...provided2.draggableProps} {...provided2.dragHandleProps}
                                                                    style={ rowStyle(snapshot2.isDragging, isSelected, provided2.draggableProps.style) }
                                                                    className={isSelected ? 'row-selected' : ''} >
                                                                    <td style={cellStyle}>
                                                                        <CheckboxField label="" labelHidden name={`select-${rowId}`} value={rowId}
                                                                                       checked={isSelected} onChange={(e) => handleSelectRow(rowId, e.target.checked)}
                                                                                       size="small" />
                                                                    </td>
                                                                    <td style={{ ...cellStyle, fontWeight: 'bold' }}>{displayId}</td>
                                                                    <td style={cellStyle}>{orderDateStr}</td>
                                                                    <td style={cellStyle}>{row.OrderStatus || 'N/A'}</td>
                                                                    <td style={cellStyle}>{lineQty}</td>
                                                                    <td style={cellStyle}>{row.Board_Grade}</td>
                                                                    <td style={cellStyle}>{row.Box_Style}</td>
                                                                    <td style={cellStyle}>{hrsFmt}</td>
                                                                    <td style={cellStyle}>{row.notes}</td> {/* GT Narration */}
                                                                    <td style={cellStyle}>{row.adminNotes}</td> {/* New Notes */}
                                                                    <td style={cellStyle}>
                                                                        <Flex direction="row" gap="xs" wrap="nowrap">
                                                                            <Link to={`/orders/${encodeURIComponent(row.orderId)}`}>
                                                                                <Button size="small" variation="link">View</Button>
                                                                            </Link>
                                                                            <Button size="small" onClick={() => generateJobSheetPDF(row)}>PDF</Button>
                                                                        </Flex>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                                </tbody>
                                            </table>
                                        </div> // End scroll wrapper
                                    )}
                                </Droppable>
                            </View>
                        );
                    })
                )}
            </DragDropContext>

            {filteredJobs.length > 0 && renderControls()}
        </View>
    );
}


// --- Styles ---
const cellStyle = {
    border: '1px solid #e0e0e0',
    padding: '6px 8px',
    textAlign: 'left',
    verticalAlign: 'middle',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap', // Prevent wrapping in cells initially
};
const tableStyle = (isDraggingOver) => ({
    width: '100%',
    minWidth: '1000px', // Ensure table has minimum width for horizontal scroll
    borderCollapse: 'collapse',
    marginTop: '0.5rem',
    background: isDraggingOver ? '#e6f7ff' : 'transparent',
    border: isDraggingOver ? '2px dashed #91d5ff' : 'none',
    tableLayout: 'auto', // Allow columns to size based on content
});
const rowStyle = (isDragging, isSelected, draggableStyle) => ({
    background: isDragging ? '#d9f7be' : (isSelected ? '#fffbe6' : 'white'),
    cursor: isDragging ? 'grabbing' : 'grab',
    ...draggableStyle,
});
const emptyRowStyle = (isDraggingOver) => ({
    ...cellStyle,
    textAlign: 'center',
    fontStyle: 'italic',
    padding: '10px',
    color: '#888',
    background: isDraggingOver ? '#e6f7ff' : '#fafafa',
    whiteSpace: 'normal', // Allow wrapping for the empty message
});